import { Dialog, DialogBackdrop, Tab, TabGroup, TabList, TabPanel, TabPanels, Transition, TransitionChild } from "@headlessui/react";
import { useState } from "react";
import { getEndpoint } from "../pages/sources/utils";
import { Button, Card, Badge, TextInput, Textarea, Switch } from "@tremor/react";
import {
  ClipboardDocumentIcon,
  DocumentArrowDownIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon
} from "@heroicons/react/24/outline";

function Drawer({ isOpen, onClose, rowData, onSave }) {
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [sourceName, setSourceName] = useState(rowData?.name || '');
  const [sourceDescription, setSourceDescription] = useState(rowData?.description || '');
  const [sourceEnabled, setSourceEnabled] = useState(rowData?.enabled !== false);
  const [logFilter, setLogFilter] = useState('');
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [replayingLog, setReplayingLog] = useState(null);
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const tabs = [
    { name: 'Overview', id: 'overview' },
    { name: 'Endpoint', id: 'endpoint' },
    { name: 'Logs', id: 'logs' },
    { name: 'Settings', id: 'settings' }
  ];

  const getStatusBadge = () => {
    if (!rowData) return null;
    
    // Check for various possible timestamp fields and event count
    const lastEvent = rowData.lastEventAt || rowData.lastEvent || rowData.updatedAt;
    const eventCount = rowData.eventCount || 0;
    const now = new Date();
    const lastEventDate = lastEvent ? new Date(lastEvent) : null;
    const hoursSinceLastEvent = lastEventDate ? (now - lastEventDate) / (1000 * 60 * 60) : null;
    
    if (!sourceEnabled) {
      return <Badge color="gray" size="sm">Disabled</Badge>;
    } else if (eventCount === 0 || (!lastEventDate && eventCount === 0)) {
      return <Badge color="yellow" size="sm">No Events</Badge>;
    } else if (eventCount > 0 && !lastEventDate) {
      // Has events but no timestamp - assume active
      return <Badge color="green" size="sm">Active</Badge>;
    } else if (hoursSinceLastEvent < 1) {
      return <Badge color="green" size="sm">Active</Badge>;
    } else if (hoursSinceLastEvent < 24) {
      return <Badge color="yellow" size="sm">Recent</Badge>;
    } else {
      return <Badge color="red" size="sm">Inactive</Badge>;
    }
  };

  const copyToClipboard = async (text, setCopiedState) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedState(true);
      setTimeout(() => setCopiedState(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getCurlExample = () => {
    const endpoint = getEndpoint(rowData);
    return `curl -X POST "${endpoint}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "deviceId": "your-device-id",
    "data": {
      "temperature": 25.5,
      "humidity": 60
    }
  }'`;
  };

  const formatLogTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getLogPreview = (log, maxLength = 100) => {
    const logString = typeof log === 'string' ? log : JSON.stringify(log);
    if (logString.length <= maxLength) return logString;
    return logString.substring(0, maxLength) + '...';
  };

  const getLogSummary = (log) => {
    if (typeof log === 'string') {
      try {
        const parsed = JSON.parse(log);
        return getObjectSummary(parsed);
      } catch {
        return log.length > 50 ? log.substring(0, 50) + '...' : log;
      }
    }
    return getObjectSummary(log);
  };

  const getObjectSummary = (obj) => {
    if (!obj || typeof obj !== 'object') return String(obj);
    
    const keys = Object.keys(obj);
    if (keys.length === 0) return '{}';
    
    if (keys.length <= 3) {
      return `{ ${keys.join(', ')} }`;
    }
    
    return `{ ${keys.slice(0, 3).join(', ')}, +${keys.length - 3} more }`;
  };

  const toggleLogExpansion = (index) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedLogs(newExpanded);
  };

  const filteredLogs = rowData?.logs?.filter(log => 
    !logFilter || JSON.stringify(log).toLowerCase().includes(logFilter.toLowerCase())
  ) || [];

  const downloadLogs = () => {
    const logsText = JSON.stringify(rowData?.logs || [], null, 2);
    const blob = new Blob([logsText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${rowData?.name || 'source'}-logs.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const testEndpoint = async () => {
    // Placeholder for endpoint testing functionality
    alert('Test endpoint functionality would be implemented here');
  };

  const replayLogMessage = async (log, index) => {
    setReplayingLog(index);
    
    try {
      const endpoint = getEndpoint(rowData);
      
      // Extract and properly parse the message data from the log
      let messageData;
      
      if (typeof log === 'string') {
        // Handle escaped JSON strings
        let cleanedString = log;
        
        // Remove outer quotes if they exist
        if (cleanedString.startsWith('"') && cleanedString.endsWith('"')) {
          cleanedString = cleanedString.slice(1, -1);
        }
        
        // Unescape common JSON escape sequences
        cleanedString = cleanedString
          .replace(/\\"/g, '"')     // Unescape quotes
          .replace(/\\\\/g, '\\')   // Unescape backslashes
          .replace(/\\n/g, '\n')    // Unescape newlines
          .replace(/\\r/g, '\r')    // Unescape carriage returns
          .replace(/\\t/g, '\t');   // Unescape tabs
        
        try {
          messageData = JSON.parse(cleanedString);
        } catch {
          // If parsing fails, try the original string
          try {
            messageData = JSON.parse(log);
          } catch {
            // If all parsing fails, wrap in data object
            messageData = { data: log };
          }
        }
      } else if (log.message) {
        // Handle message property
        if (typeof log.message === 'string') {
          try {
            messageData = JSON.parse(log.message);
          } catch {
            messageData = log.message;
          }
        } else {
          messageData = log.message;
        }
      } else {
        messageData = log;
      }

      console.log('Replaying message:', messageData);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageData)
      });

      if (response.ok) {
        alert('✅ Message replayed successfully!');
      } else {
        const errorText = await response.text();
        alert(`❌ Replay failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Replay error:', error);
      alert(`❌ Replay failed: ${error.message}`);
    } finally {
      setReplayingLog(null);
    }
  };

  const saveChanges = async () => {
    setSaving(true);
    
    try {
      // Prepare only the fields we want to update (not the entire object)
      const updatedSource = {
        name: sourceName,
        description: sourceDescription,
        enabled: sourceEnabled
      };

      console.log('Saving source data:', updatedSource);

      // Call the onSave callback if provided
      if (onSave) {
        await onSave({ ...rowData, ...updatedSource });
        alert('✅ Changes saved successfully!');
      } else {
        // Fallback: make API call directly
        const response = await fetch(`/api/sources/${rowData._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('oauth_token') || localStorage.getItem('jwt')}`
          },
          body: JSON.stringify(updatedSource)
        });

        if (response.ok) {
          alert('✅ Changes saved successfully!');
        } else {
          const errorText = await response.text();
          console.error('API Error:', response.status, errorText);
          alert(`❌ Save failed: ${response.status} - ${errorText}`);
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      alert(`❌ Save failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Transition show={isOpen}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden z-50"
        onClose={onClose}
      >
        <div className="absolute inset-0 overflow-hidden">
          <DialogBackdrop className="absolute inset-0 bg-black bg-opacity-30" />

          <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-2xl">
                <div className="flex flex-col h-full bg-white dark:bg-gray-900 shadow-xl">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">
                          {rowData ? rowData.name : "Source Details"}
                        </h2>
                        {getStatusBadge()}
                      </div>
                      <button
                        onClick={onClose}
                        className="rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>

                  {/* Tabs */}
                  <TabGroup>
                    <div className="border-b border-gray-200 dark:border-gray-800">
                      <TabList className="flex space-x-8 px-6">
                        {tabs.map((tab) => (
                          <Tab
                            key={tab.id}
                            className={({ selected }) =>
                              `py-3 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                                selected
                                  ? 'border-blue-500 text-blue-600'
                                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-800'
                              }`
                            }
                          >
                            {tab.name}
                          </Tab>
                        ))}
                      </TabList>
                    </div>

                    {/* Tab Panels */}
                    <div className="flex-1 overflow-y-auto">
                      <TabPanels className="h-full">
                        {/* Overview Tab */}
                        <TabPanel className="p-6 space-y-6">
                          <div className="grid grid-cols-1 gap-6">
                            {/* Status Card */}
                            <Card className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50">Status</h3>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">Current source status</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <SignalIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                                  {getStatusBadge()}
                                </div>
                              </div>
                            </Card>

                            {/* Statistics Cards */}
                            <div className="grid grid-cols-2 gap-4">
                              <Card className="p-4">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <div className="h-8 w-8 bg-blue-100 dark:bg-blue-950/40 rounded-lg flex items-center justify-center">
                                      <SignalIcon className="h-5 w-5 text-blue-600" />
                                    </div>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Events</p>
                                    <p className="text-2xl font-semibold text-gray-900 dark:text-gray-50">
                                      {rowData?.eventCount || 0}
                                    </p>
                                  </div>
                                </div>
                              </Card>

                              <Card className="p-4">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <div className="h-8 w-8 bg-green-100 dark:bg-green-950/40 rounded-lg flex items-center justify-center">
                                      <ClockIcon className="h-5 w-5 text-green-600" />
                                    </div>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Event</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                                      {(() => {
                                        const lastEvent = rowData?.lastEventAt || rowData?.lastEvent || rowData?.updatedAt;
                                        const eventCount = rowData?.eventCount || 0;
                                        
                                        if (eventCount > 0 && !lastEvent) {
                                          return 'Recent activity';
                                        } else if (lastEvent) {
                                          return formatLogTime(lastEvent);
                                        } else {
                                          return 'Never';
                                        }
                                      })()}
                                    </p>
                                  </div>
                                </div>
                              </Card>
                            </div>

                            {/* Source Information */}
                            <Card className="p-4 space-y-4">
                              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50">Source Information</h3>
                              
                              <div className="space-y-3">
                                <div>
                                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Provider</label>
                                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-50">{rowData?.provider || 'N/A'}</p>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</label>
                                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-50">
                                    {rowData?.createdAt ? formatLogTime(rowData.createdAt) : 'Unknown'}
                                  </p>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Modified</label>
                                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-50">
                                    {rowData?.updatedAt ? formatLogTime(rowData.updatedAt) : 'Unknown'}
                                  </p>
                                </div>
                              </div>
                            </Card>
                          </div>
                        </TabPanel>

                        {/* Endpoint Tab */}
                        <TabPanel className="p-6 space-y-6">
                          {/* Main Endpoint */}
                          <Card className="p-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50 mb-4">HTTP Endpoint</h3>
                            <div className="space-y-3">
                              <div>
                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">URL</label>
                                <div className="mt-1 relative">
                                  <textarea
                                    className="w-full p-3 text-sm font-mono border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    value={getEndpoint(rowData)}
                                    readOnly
                                    onClick={(e) => e.target.select()}
                                    rows={2}
                                  />
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    icon={ClipboardDocumentIcon}
                                    className="absolute bottom-2 right-2"
                                    onClick={() => copyToClipboard(getEndpoint(rowData), setCopiedEndpoint)}
                                  >
                                    {copiedEndpoint ? 'Copied!' : 'Copy'}
                                  </Button>
                                </div>
                              </div>

                            </div>
                          </Card>

                          {/* cURL Example */}
                          {/* <Card className="p-4">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50">cURL Example</h3>
                              <Button
                                size="sm"
                                variant="secondary"
                                icon={ClipboardDocumentIcon}
                                onClick={() => copyToClipboard(getCurlExample(), setCopiedCurl)}
                              >
                                {copiedCurl ? 'Copied!' : 'Copy'}
                              </Button>
                            </div>
                            <pre className="w-full p-3 text-sm font-mono bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 rounded-lg overflow-x-auto">
                              {getCurlExample()}
                            </pre>
                          </Card> */}

                          {/* Integration Notes */}
                          <Card className="p-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50 mb-3">Integration Notes</h3>
                            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                              <p>• Send data via HTTP POST requests to the endpoint above</p>
                              <p>• Content-Type should be application/json</p>
                              <p>• Include your API key in the URL parameters</p>
                              <p>• Data should include deviceId and measurement data</p>
                            </div>
                          </Card>
                        </TabPanel>

                        {/* Logs Tab */}
                        <TabPanel className="flex flex-col h-full">
                          <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 dark:border-gray-800">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50">Event Logs</h3>
                            <div className="flex items-center space-x-2">
                              <TextInput
                                placeholder="Filter logs..."
                                value={logFilter}
                                onChange={(e) => setLogFilter(e.target.value)}
                                className="w-48"
                              />
                              <Button
                                size="sm"
                                variant="secondary"
                                icon={DocumentArrowDownIcon}
                                onClick={downloadLogs}
                              >
                                Download
                              </Button>
                            </div>
                          </div>

                          <div className="flex-1 overflow-hidden">
                            <div className="h-full overflow-y-auto">
                              {filteredLogs.length > 0 ? (
                                <div className="divide-y divide-gray-200 dark:divide-gray-800">
                                  {filteredLogs.slice(0, 100).map((log, index) => {
                                    const isExpanded = expandedLogs.has(index);
                                    return (
                                      <div key={index} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800">
                                        <div className="flex items-start justify-between gap-3">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2 mb-2">
                                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                                {log.timestamp ? formatLogTime(log.timestamp) : 'Unknown time'}
                                              </span>
                                              <Badge 
                                                color={log.level === 'error' ? 'red' : log.level === 'warn' ? 'yellow' : 'green'} 
                                                size="xs"
                                              >
                                                {log.level || 'info'}
                                              </Badge>
                                            </div>
                                            
                                            {/* Compact Summary */}
                                            {!isExpanded && (
                                              <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                                <span className="font-medium">{getLogSummary(log)}</span>
                                              </div>
                                            )}
                                            
                                            {/* Full Raw Message */}
                                            {isExpanded && (
                                              <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 mb-2 max-w-full overflow-x-auto">
                                                <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all">
                                                  {typeof log === 'string' ? log : JSON.stringify(log, null, 2)}
                                                </pre>
                                              </div>
                                            )}
                                            
                                            {/* Expand/Collapse Button */}
                                            <button
                                              onClick={() => toggleLogExpansion(index)}
                                              className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1"
                                            >
                                              {isExpanded ? (
                                                <>
                                                  <EyeSlashIcon className="h-3 w-3" />
                                                  <span>Hide raw</span>
                                                </>
                                              ) : (
                                                <>
                                                  <EyeIcon className="h-3 w-3" />
                                                  <span>Show raw</span>
                                                </>
                                              )}
                                            </button>
                                          </div>
                                          
                                          <div className="flex-shrink-0">
                                            <Button
                                              size="xs"
                                              variant="secondary"
                                              icon={ArrowPathIcon}
                                              onClick={() => replayLogMessage(log, index)}
                                              loading={replayingLog === index}
                                              disabled={replayingLog !== null}
                                              title="Replay this message to the source endpoint"
                                              className="text-xs"
                                            >
                                              {replayingLog === index ? 'Replaying...' : 'Replay'}
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                                  <div className="text-center">
                                    <ExclamationTriangleIcon className="h-8 w-8 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
                                    <p>No logs found</p>
                                    {logFilter && <p className="text-sm">Try adjusting your filter</p>}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </TabPanel>

                        {/* Settings Tab */}
                        <TabPanel className="p-6 space-y-6">
                          <Card className="p-4 space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50">Basic Settings</h3>
                            
                            {/* Source Name */}
                            <div>
                              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Source Name</label>
                              <div className="mt-1 flex items-center space-x-2">
                                {editingName ? (
                                  <>
                                    <TextInput
                                      value={sourceName}
                                      onChange={(e) => setSourceName(e.target.value)}
                                      className="flex-1"
                                    />
                                    <Button size="sm" icon={CheckIcon} onClick={() => setEditingName(false)}>
                                      Save
                                    </Button>
                                    <Button size="sm" variant="secondary" icon={XMarkIcon} onClick={() => {
                                      setSourceName(rowData?.name || '');
                                      setEditingName(false);
                                    }}>
                                      Cancel
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <span className="flex-1 text-sm text-gray-900 dark:text-gray-50">{sourceName}</span>
                                    <Button size="sm" variant="secondary" icon={PencilIcon} onClick={() => setEditingName(true)}>
                                      Edit
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Source Description */}
                            <div>
                              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</label>
                              <div className="mt-1">
                                {editingDescription ? (
                                  <div className="space-y-2">
                                    <Textarea
                                      value={sourceDescription}
                                      onChange={(e) => setSourceDescription(e.target.value)}
                                      placeholder="Add a description for this source..."
                                      rows={3}
                                    />
                                    <div className="flex space-x-2">
                                      <Button size="sm" icon={CheckIcon} onClick={() => setEditingDescription(false)}>
                                        Save
                                      </Button>
                                      <Button size="sm" variant="secondary" icon={XMarkIcon} onClick={() => {
                                        setSourceDescription(rowData?.description || '');
                                        setEditingDescription(false);
                                      }}>
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-start justify-between">
                                    <p className="text-sm text-gray-900 dark:text-gray-50 flex-1">
                                      {sourceDescription || 'No description provided'}
                                    </p>
                                    <Button size="sm" variant="secondary" icon={PencilIcon} onClick={() => setEditingDescription(true)}>
                                      Edit
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Enable/Disable */}
                            <div className="flex items-center justify-between py-2">
                              <div>
                                <label className="text-sm font-medium text-gray-900 dark:text-gray-50">Enable Source</label>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Allow this source to receive data</p>
                              </div>
                              <Switch
                                checked={sourceEnabled}
                                onChange={setSourceEnabled}
                              />
                            </div>
                          </Card>

                    
                        </TabPanel>
                      </TabPanels>
                    </div>
                  </TabGroup>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                    <div className="flex space-x-3">
                      <Button onClick={onClose} variant="secondary" className="flex-1">
                        Close
                      </Button>
                      <Button 
                        onClick={saveChanges}
                        loading={saving}
                        disabled={saving}
                        className="flex-1"
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default Drawer;
